import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Smartphone, X } from 'lucide-react';
import { useIsMobilePortrait } from '@/hooks/use-mobile';
import { useState } from 'react';

// 手机竖屏「请横屏」提示浮窗：铺满的半透明遮罩 + 居中卡片。
// 仅在窄屏纵向(useIsMobilePortrait)时渲染 —— 转到横屏后现有 844×390 舞台接管、本浮窗自动消失,
// 转回竖屏再现。dismissed 仅本次 session 有效，旋转后再次出现可再次关闭。
// 挂载点见 Dashboard.tsx(isStudent 门控),portal 到 body 以浮于一切之上。

export default function LandscapeHint() {
  const { t } = useTranslation();
  const show = useIsMobilePortrait();
  const [dismissed, setDismissed] = useState(false);
  if (!show || dismissed) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      {/* 图标「竖→横」摆动动画,直观传达「旋转」 */}
      <style>{`@keyframes landscapeHintRotate{0%,40%{transform:rotate(0deg)}70%,100%{transform:rotate(90deg)}}`}</style>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          maxWidth: 320,
          padding: '32px 28px',
          borderRadius: 24,
          background: '#fff',
          boxShadow: '0 12px 40px -8px rgba(0,0,0,0.4)',
          textAlign: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => setDismissed(true)}
          // 热区 28→44(触控下限)，top/right 12→4 补偿：中心距边仍 26(12+14 → 4+22)，图标位置不变
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            // 不设 border-radius：背景透明时圆角无任何视觉作用，但会把命中区裁成直径 44 的圆(四角点不到)。
            border: 0,
            background: 'transparent',
            color: '#9CA3AF',
            cursor: 'pointer',
          }}
          aria-label={t('common.close', { defaultValue: '關閉' })}
          title={t('common.close', { defaultValue: '關閉' })}
        >
          <X size={18} />
        </button>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'color-mix(in srgb, var(--primary) 14%, transparent)',
            color: 'var(--primary)',
          }}
        >
          <Smartphone
            size={36}
            style={{
              transformOrigin: 'center',
              animation: 'landscapeHintRotate 2.4s ease-in-out infinite',
            }}
          />
        </span>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#111', fontFamily: 'var(--font-sans)' }}>
          {t('common.landscape-hint-title')}
        </div>
        <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, fontFamily: 'var(--font-sans)' }}>
          {t('common.landscape-hint-desc')}
        </div>
      </div>
    </div>,
    document.body,
  );
}
